import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import download from '../../images/icons/download-gray.svg';
import UpArrowImage from "../../images/icons/up-down-arrow.svg";
import DownArrowImage from "../../images/icons/up-down-arrow.svg";
import { useSubscriptionPayments, useDownloadInvoice } from './useSubscriptionQueries';

// Add CSS for spinner animation
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const SubscriptionOverview = () => {
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const token = useSelector((state) => state.auth.token);

  const [sort, setSort] = useState({
    column: 'billingDate',
    direction: 'desc',
  });

  // Use React Query hook for subscription payments
  const { 
    data = [], 
    isLoading: loading, 
    error: paymentsError 
  } = useSubscriptionPayments(token);

  const downloadInvoiceMutation = useDownloadInvoice();

  const sortedData = useMemo(() => {
    if (!sort.column) return data;
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aValue = a[sort.column];
      let bValue = b[sort.column];

      // Handle date sorting
      if (sort.column === 'billingDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle amount sorting (remove currency symbols)
      if (sort.column === 'amount') {
        aValue = parseFloat(aValue.replace(/[^0-9.]/g, '')) || 0;
        bValue = parseFloat(bValue.replace(/[^0-9.]/g, '')) || 0;
      }

      if (sort.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    return sorted;
  }, [sort.column, sort.direction, data]);

  const handleSort = (column) => {
    if (sort.column === column) {
      setSort({ column, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ column, direction: 'asc' });
    }
  };

  const handleDownloadInvoice = async (paymentId) => {
    if (!token) {
      toast.error('Please log in to download invoice', { position: "bottom-right", autoClose: 3000 });
      return;
    }

    setDownloadingInvoiceId(paymentId);

    try {
      const response = await downloadInvoiceMutation.mutateAsync({
        token,
        paymentId
      });

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'subscription_invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Ensure .pdf extension
          if (!filename.endsWith('.pdf')) {
            filename = filename.replace(/\.pdf_?$/, '') + '.pdf';
          }
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Invoice downloaded successfully', { position: "bottom-right", autoClose: 2000 });
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error(
        error?.response?.data?.message || error.message || 'Failed to download invoice. Please try again.',
        { position: "bottom-right", autoClose: 3000 }
      );
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const getSortIcon = (column) => {
    if (sort.column === column) {
      return (
        <span>
          {sort.direction === 'asc' ? (
            <img src={UpArrowImage} alt="ascending" style={{ width: 12, height: 12 }} />
          ) : (
            <img src={DownArrowImage} alt="descending" style={{ width: 12, height: 12 }} />
          )}
        </span>
      );
    }
    return <img src={UpArrowImage} alt="sort" style={{ width: 12, height: 12 }} />;
  };

  if (loading) {
    return (
      <>
        <style>{spinnerStyle}</style>
        <div className="card-outer1">
          <div className="time-table">
            <table style={{ minWidth: "50rem", width: "100%", textAlign: "left" }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: "27px", paddingTop: "11px", paddingBottom: "12px", background: "#f5f6f8", borderTopLeftRadius: "10px" }}>Invoice ID</th>
                  <th style={{ paddingTop: "11px", paddingBottom: "12px", background: "#f5f6f8" }}>Plan</th>
                  <th style={{ paddingTop: "11px", paddingBottom: "12px", background: "#f5f6f8" }}>Billing Date</th>
                  <th style={{ paddingTop: "11px", paddingBottom: "12px", background: "#f5f6f8" }}>Amount</th>
                  <th style={{ paddingTop: "11px", paddingBottom: "12px", background: "#f5f6f8" }}>Payment Status</th>
                  <th className="subscription-download-icon" style={{ paddingTop: "11px", paddingBottom: "12px", paddingRight: "8px", background: "#f5f6f8", borderTopRightRadius: "10px" }}></th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`}>
                    {/* Invoice ID */}
                    <td style={{ paddingLeft: "27px", paddingTop: "19px", paddingBottom: "22px", borderBottom: "0.5px solid #d8dde1" }}>
                      <div
                        style={{
                          width: "120px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Plan */}
                    <td style={{ paddingTop: "19px", paddingBottom: "22px", borderBottom: "0.5px solid #d8dde1" }}>
                      <div
                        style={{
                          width: "150px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Billing Date */}
                    <td style={{ paddingTop: "19px", paddingBottom: "22px", borderBottom: "0.5px solid #d8dde1" }}>
                      <div
                        style={{
                          width: "120px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Amount */}
                    <td style={{ paddingTop: "19px", paddingBottom: "22px", borderBottom: "0.5px solid #d8dde1" }}>
                      <div
                        style={{
                          width: "100px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Payment Status */}
                    <td style={{ paddingTop: "19px", paddingBottom: "22px", borderBottom: "0.5px solid #d8dde1" }}>
                      <div
                        style={{
                          width: "80px",
                          height: "24px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </td>
                    {/* Download Icon */}
                    <td className="subscription-download-icon" style={{ paddingTop: "19px", paddingBottom: "22px", paddingRight: "8px", borderBottom: "0.5px solid #d8dde1" }}>
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                          margin: "0 auto",
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  if (paymentsError && !loading) {
    return (
      <div className="card-outer1">
        <div className="time-table">
          <div style={{ textAlign: "center", padding: "2rem", color: "#ef4444" }}>
            {paymentsError?.response?.data?.message || paymentsError?.message || 'Failed to load subscription payments'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{spinnerStyle}</style>
      <div className="card-outer1">
        <div className="time-table">
        <table style={{ minWidth: "50rem", width: "100%", textAlign: "left" }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('invoiceId')}>
                Invoice ID
                {getSortIcon('invoiceId')}
              </th>
              <th onClick={() => handleSort('plan')}>
                Plan
                {getSortIcon('plan')}
              </th>
              <th onClick={() => handleSort('billingDate')}>
                Billing Date
                {getSortIcon('billingDate')}
              </th>
              <th onClick={() => handleSort('amount')}>
                Amount
                {getSortIcon('amount')}
              </th>
              <th onClick={() => handleSort('paymentStatus')}>
                Payment Status
                {getSortIcon('paymentStatus')}
              </th>
              <th className="subscription-download-icon"></th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  No Data Available
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr key={row.id || index}>
                  <td>{row.invoiceId}</td>
                  <td>{row.plan}</td>
                  <td>{row.billingDate}</td>
                  <td>{row.amount}</td>
                  <td>
                    <span
                      className={
                        row.paymentStatus === "Paid"
                          ? "payment-status manual"
                          : "payment-status pending"
                      }
                    >
                      {row.paymentStatus}
                    </span>
                  </td>
                  <td className="subscription-download-icon">
                    <button 
                      type="button" 
                      onClick={() => handleDownloadInvoice(row.id)}
                      disabled={downloadingInvoiceId === row.id}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        cursor: downloadingInvoiceId === row.id ? 'wait' : 'pointer',
                        padding: '5px',
                        opacity: downloadingInvoiceId === row.id ? 0.6 : 1
                      }}
                      title={downloadingInvoiceId === row.id ? 'Generating invoice...' : 'Download Invoice'}
                    >
                      {downloadingInvoiceId === row.id ? (
                        <div style={{ 
                          width: '16px', 
                          height: '16px', 
                          border: '2px solid #f3f3f3',
                          borderTop: '2px solid #667eea',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          display: 'inline-block'
                        }} />
                      ) : (
                        <img src={download} alt="download" />
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
};

export default SubscriptionOverview;